/**
 * Loading Component for Cedar Heights Music Academy Admin System
 * 
 * This component provides various loading states with:
 * - Skeleton screens for content placeholders
 * - Circular progress indicators
 * - Linear progress bars
 * - Full-screen loading overlays
 * - Customizable sizes and variants
 */

import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Typography,
  Backdrop,
  useTheme,
} from '@mui/material';

interface LoadingProps {
  variant?: 'circular' | 'linear' | 'skeleton' | 'overlay';
  size?: 'small' | 'medium' | 'large';
  message?: string;
  fullScreen?: boolean;
  rows?: number; // For skeleton variant
  height?: number | string; // For skeleton variant
}

/**
 * Versatile loading component that can display different types of loading indicators
 */
export const Loading: React.FC<LoadingProps> = ({
  variant = 'circular',
  size = 'medium',
  message,
  fullScreen = false,
  rows = 3,
  height = 40,
}) => {
  const theme = useTheme();

  // Size mappings for different variants
  const getSizeValue = () => {
    switch (size) {
      case 'small':
        return variant === 'circular' ? 24 : 2;
      case 'large':
        return variant === 'circular' ? 60 : 6;
      default:
        return variant === 'circular' ? 40 : 4;
    }
  };

  const renderLoadingContent = () => {
    switch (variant) {
      case 'linear':
        return (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              sx={{
                height: getSizeValue(),
                borderRadius: theme.spacing(1),
              }}
            />
            {message && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginTop: theme.spacing(1), textAlign: 'center' }}
              >
                {message}
              </Typography>
            )}
          </Box>
        );

      case 'skeleton':
        return (
          <Box sx={{ width: '100%' }}>
            {Array.from({ length: rows }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                height={height}
                sx={{
                  marginBottom: theme.spacing(1),
                  borderRadius: theme.spacing(1),
                  '&:last-child': {
                    marginBottom: 0,
                  },
                }}
              />
            ))}
          </Box>
        );

      case 'overlay':
        return (
          <Backdrop
            open={true}
            sx={{
              color: theme.palette.common.white,
              zIndex: theme.zIndex.modal + 1,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: theme.spacing(2),
              }}
            >
              <CircularProgress
                size={getSizeValue()}
                color="inherit"
                thickness={4}
              />
              {message && (
                <Typography variant="h6" color="inherit">
                  {message}
                </Typography>
              )}
            </Box>
          </Backdrop>
        );

      default: // circular
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: theme.spacing(2),
              ...(fullScreen && {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: theme.zIndex.modal,
                justifyContent: 'center',
              }),
            }}
          >
            <CircularProgress
              size={getSizeValue()}
              thickness={4}
              sx={{
                color: theme.palette.primary.main,
              }}
            />
            {message && (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: 'center' }}
              >
                {message}
              </Typography>
            )}
          </Box>
        );
    }
  };

  if (variant === 'overlay') {
    return renderLoadingContent();
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2),
        width: '100%',
      }}
    >
      {renderLoadingContent()}
    </Box>
  );
};

/**
 * Skeleton loading component for list items
 */
export const ListItemSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => {
  const theme = useTheme();

  return (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(2),
            gap: theme.spacing(2),
          }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
          </Box>
          <Skeleton variant="rectangular" width={80} height={32} />
        </Box>
      ))}
    </Box>
  );
};

/**
 * Card skeleton loading component
 */
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: theme.spacing(3),
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Box
          key={index}
          sx={{
            padding: theme.spacing(3),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.spacing(2),
          }}
        >
          <Skeleton variant="text" width="80%" height={32} />
          <Skeleton variant="text" width="60%" height={24} sx={{ marginY: theme.spacing(1) }} />
          <Skeleton variant="rectangular" width="100%" height={120} sx={{ marginY: theme.spacing(2) }} />
          <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
            <Skeleton variant="rectangular" width={80} height={32} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default Loading;